import { MoreHorizontalIcon, PencilIcon, CalendarIcon, Trash2Icon } from 'lucide-react'
import { AvatarRastro, Logo } from '@/components/marca/Logo'
import { useAvisar } from '@/components/ui/avisador-contexto'
import { Boton } from '@/components/ui/Boton'
import { ChipEstado, type EstadoPublicacion } from '@/components/ui/ChipEstado'
import {
  Dialogo,
  DialogoContenido,
  DialogoCuerpo,
  DialogoDescripcion,
  DialogoDisparador,
  DialogoEncabezado,
  DialogoPie,
  DialogoTitulo,
  DialogoCerrar,
} from '@/components/ui/Dialogo'
import {
  MenuContenido,
  MenuDesplegable,
  MenuDisparador,
  MenuItem,
  MenuSeparador,
} from '@/components/ui/MenuDesplegable'
import { MediaSlot } from '@/features/publicacion/MediaSlot'

const ESTADOS: EstadoPublicacion[] = ['borrador', 'para_revisar', 'cambios', 'aprobada']

// Clases estáticas: Tailwind sólo genera lo que encuentra escrito.
const PILARES = [
  'bg-pilar-1',
  'bg-pilar-2',
  'bg-pilar-3',
  'bg-pilar-4',
  'bg-pilar-5',
  'bg-pilar-6',
  'bg-pilar-7',
  'bg-pilar-8',
]

// Un blurhash de muestra (tonos cálidos, como una foto de café).
const BLURHASH_MUESTRA = 'LGF5]+Yk^6#M@-5c,1J5@[or[Q6.'

/**
 * Muestrario del sistema visual — Fase 0.
 * Existe para verificar tokens, tipografía, logos y componentes base en los
 * dos formatos antes de construir pantallas. En la Fase 1 la raíz pasa a ser
 * el ingreso (P00) y este muestrario queda en /muestrario fuera de producción.
 */
export function Muestrario() {
  const avisar = useAvisar()

  return (
    <main className="mx-auto flex min-h-dvh max-w-3xl flex-col gap-6 px-4 py-8 pb-24">
      <header className="flex items-center justify-between">
        <Logo variante="primario" className="hidden h-9 sm:block" />
        <Logo variante="corto" className="h-9 sm:hidden" />
        <div className="flex items-center gap-3">
          <span
            className="inline-flex size-9 items-center justify-center rounded-full bg-borde text-sm font-semibold"
            aria-label="Valentina"
          >
            VA
          </span>
          <AvatarRastro />
        </div>
      </header>

      <section className="rounded-tarjeta bg-tarjeta p-6 shadow-tarjeta sm:p-8">
        <h1 className="text-2xl font-semibold tracking-tight">Portal RASTRO</h1>
        <p className="mt-2 text-tinta-suave">
          Sistema visual listo: tokens, Inter, logos oficiales y componentes base. Las
          pantallas se construyen según el inventario del §4.
        </p>
      </section>

      <section className="rounded-tarjeta bg-tarjeta p-6 shadow-tarjeta">
        <h2 className="text-sm font-medium text-tinta-suave">Estados de publicación</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {ESTADOS.map((estado) => (
            <ChipEstado key={estado} estado={estado} />
          ))}
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {ESTADOS.map((estado) => (
            <ChipEstado key={estado} estado={estado} forma="corta" />
          ))}
        </div>
      </section>

      <section className="rounded-tarjeta bg-tarjeta p-6 shadow-tarjeta">
        <h2 className="text-sm font-medium text-tinta-suave">Pilares</h2>
        <div className="mt-3 flex flex-wrap gap-3">
          {PILARES.map((clase, i) => (
            <span
              key={clase}
              className={`size-10 rounded-full ${clase}`}
              aria-label={`Color de pilar ${String(i + 1)}`}
            />
          ))}
        </div>
      </section>

      <section className="rounded-tarjeta bg-tarjeta p-6 shadow-tarjeta">
        <h2 className="text-sm font-medium text-tinta-suave">Acciones</h2>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <Boton
            onClick={() => {
              avisar('Aprobada', {
                accion: {
                  etiqueta: 'Deshacer',
                  alHacer: () => {
                    avisar('Deshecho')
                  },
                },
              })
            }}
          >
            Aprobar
          </Boton>
          <Boton variante="secundario">Pedir cambios</Boton>
          <Boton variante="sutil">Mostrar más</Boton>
          <Boton disabled>Enviar a revisión</Boton>

          <Dialogo>
            <DialogoDisparador asChild>
              <Boton variante="secundario">Abrir modal</Boton>
            </DialogoDisparador>
            <DialogoContenido>
              <DialogoEncabezado>
                <DialogoTitulo className="text-xl font-semibold">
                  Nuevo pilar
                </DialogoTitulo>
                <DialogoDescripcion className="text-sm text-tinta-suave">
                  Único en el proyecto.
                </DialogoDescripcion>
              </DialogoEncabezado>
              <DialogoCuerpo>
                <p className="text-sm">Contenido del modal.</p>
              </DialogoCuerpo>
              <DialogoPie>
                <DialogoCerrar asChild>
                  <Boton variante="secundario">Cancelar</Boton>
                </DialogoCerrar>
                <Boton>Guardar</Boton>
              </DialogoPie>
            </DialogoContenido>
          </Dialogo>

          <Dialogo>
            <DialogoDisparador asChild>
              <Boton variante="secundario">Abrir modal grande</Boton>
            </DialogoDisparador>
            <DialogoContenido tamano="grande">
              <DialogoEncabezado>
                <DialogoTitulo className="text-xl font-semibold">
                  Nueva publicación
                </DialogoTitulo>
                <DialogoDescripcion className="text-sm text-tinta-suave">
                  Hoja alta en celular, tarjeta ancha en escritorio.
                </DialogoDescripcion>
              </DialogoEncabezado>
              <DialogoCuerpo>
                <p className="text-sm">
                  Acá va el formulario. Salidas en celular: tocar arriba, deslizar la
                  manija hacia abajo, el gesto “atrás” del teléfono, o Cancelar en el pie.
                </p>
              </DialogoCuerpo>
              <DialogoPie>
                <DialogoCerrar asChild>
                  <Boton variante="secundario">Cancelar</Boton>
                </DialogoCerrar>
                <Boton>Guardar borrador</Boton>
              </DialogoPie>
            </DialogoContenido>
          </Dialogo>

          <MenuDesplegable>
            <MenuDisparador asChild>
              <Boton variante="secundario" tamano="icono" aria-label="Más acciones">
                <MoreHorizontalIcon className="size-5" />
              </Boton>
            </MenuDisparador>
            <MenuContenido>
              <MenuItem>
                <PencilIcon className="size-4" /> Editar
              </MenuItem>
              <MenuItem>
                <CalendarIcon className="size-4" /> Reprogramar
              </MenuItem>
              <MenuSeparador className="my-1 h-px bg-borde" />
              <MenuItem peligro>
                <Trash2Icon className="size-4" /> Dar de baja
              </MenuItem>
            </MenuContenido>
          </MenuDesplegable>
        </div>
      </section>

      <section className="rounded-tarjeta bg-tarjeta p-6 shadow-tarjeta">
        <h2 className="text-sm font-medium text-tinta-suave">
          MediaSlot (sólo degradado)
        </h2>
        <div className="mt-3 grid grid-cols-3 gap-3">
          <MediaSlot blurhash={BLURHASH_MUESTRA} className="rounded-2xl" />
          <MediaSlot className="rounded-2xl" />
          <MediaSlot blurhash={BLURHASH_MUESTRA} aspect="1:1" className="rounded-2xl" />
        </div>
      </section>

      <footer className="flex items-center justify-between border-t border-borde pt-4 text-sm text-tinta-suave">
        <span>Menos ruido, más historias</span>
        <span>{new Date().getFullYear()}</span>
      </footer>
    </main>
  )
}
